import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

/** Merge class names, with later Tailwind utilities winning over earlier ones.
 *  `cn('p-2', cond && 'p-4')` yields 'p-4' rather than both. */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/** Deterministic avatar colour from a name.
 *
 *  FNV-1a, ported verbatim from the previous library so a person keeps the same
 *  colour across the console, the signup page and this app. Changing the hash
 *  would silently re-colour everybody.
 */
export function avatarColor(name: string): string {
  let h = 0x811c9dc5
  for (let i = 0; i < name.length; i++) {
    h = (h ^ name.charCodeAt(i)) >>> 0
    h = Math.imul(h, 0x01000193) >>> 0
  }
  return `var(--cn-av-${(h % 6) + 1})`
}

/** Initials for an avatar fallback — first and last word, at most two letters. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

/** Relative time, Facebook-style: 5m, 3h, 2d, then a date.
 *
 *  Clamps negatives to "now": clock skew between the server and the browser
 *  otherwise renders a post as "in 3 seconds", which reads as a bug.
 */
export function timeAgo(iso: string): string {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''
  const secs = Math.max(0, (Date.now() - then) / 1000)

  if (secs < 60) return 'Just now'
  const mins = Math.floor(secs / 60)
  if (mins < 60) return `${mins}m`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d`

  const date = new Date(iso)
  const sameYear = date.getFullYear() === new Date().getFullYear()
  return date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  })
}

/** Compact counts, as every social product does: 1.2K, 3.4M. */
export function compactCount(n: number): string {
  if (n < 1000) return String(n)
  if (n < 1_000_000) return `${(n / 1000).toFixed(n < 10_000 ? 1 : 0)}K`.replace('.0', '')
  return `${(n / 1_000_000).toFixed(1)}M`.replace('.0', '')
}

/** "2026-08-29T18:30" — now, in the shape a datetime-local input wants.
 *
 *  For the `min` attribute on event and poll pickers, so the browser greys out
 *  every past date in its own calendar instead of letting somebody choose one
 *  and be refused afterwards.
 *
 *  NOT toISOString(). That returns UTC, so anybody east of Greenwich gets a
 *  minimum in their own past and anybody west loses hours of legitimate
 *  choices — in India it would block the rest of today until 5:30am tomorrow.
 *  Subtracting the offset first makes the ISO string read as local time, which
 *  is what the input displays and returns.
 */
export function localDateTimeNow(): string {
  const now = new Date()
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000)
  return local.toISOString().slice(0, 16)
}
